import React, { useState, FC, ReactNode, useEffect } from "react";
import { BrowserRouter, HashRouter, Routes, Route, useLocation } from "react-router-dom";
import { Sidebar } from "./components/Sidebar";
import logoPng from "./components/logo.png";
import { Header } from "./components/Header";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { FiltersProvider } from "./contexts/FiltersContext";
import HomePage from "./pages/HomePage";
import CardDetailPage from "./pages/CardDetailPage";
import DrillingChartsPage from "./pages/DrillingChartsPage";
import DrillingAnalyticsPage from "./pages/DrillingAnalyticsPage";
import { StreamsPage } from "./pages/StreamsPage";
import RecordingPlayerPage from './pages/RecordingPlayerPage';
import { StreamsProvider } from "./contexts/StreamsContext";
import ViolationsPage from "./pages/ViolationsPage";
import WellPage from './pages/WellPage';
import StatisticsPage from './pages/StatisticsPage';
import AddReportPage from './pages/AddReportPage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';

// Wrapper allows switching to HashRouter when server lacks SPA rewrites.
// Enable by building with env var: REACT_APP_USE_HASH_ROUTER=true
const RouterWrapper: FC<{ children: ReactNode }> = ({ children }) => {
  const useHash = process.env.REACT_APP_USE_HASH_ROUTER === 'true';
  const R: any = useHash ? HashRouter : BrowserRouter;
  return <R>{children}</R>;
};

const StreamsMount: FC = () => {
  const location = useLocation();
  const isStreams = location.pathname === '/streams';
  // Keep mounted and playing: overlay the page with opacity 0 instead of removing from layout
  const style = isStreams
    ? { position: 'relative' as const }
    : { position: 'fixed' as const, inset: 0 as any, opacity: 0, pointerEvents: 'none' as const, zIndex: -1 };
  return (
    <div style={style} aria-hidden={!isStreams}>
      <StreamsPage />
    </div>
  );
};

const PageMount: FC<{ path: string; children: React.ReactNode; matchPrefix?: boolean }> = ({ path, children, matchPrefix = true }) => {
  const location = useLocation();
  const isActive = matchPrefix
    ? (location.pathname === path || (path !== '/' && location.pathname.startsWith(path)))
    : location.pathname === path;
  const style = isActive
    ? { position: 'relative' as const }
    : { position: 'fixed' as const, inset: 0 as any, opacity: 0, pointerEvents: 'none' as const, zIndex: -1 };
  return (
    <div style={style} aria-hidden={!isActive}>
      {children}
    </div>
  );
};

const AppContent: FC = () => {
  const [pageTitle, setPageTitle] = useState<string>('Сводка');
  const { user } = useAuth();

  // Set favicon to match sidebar logo
  useEffect(() => {
    const setIcon = (rel: string) => {
      const link: HTMLLinkElement | null = document.querySelector(`link[rel="${rel}"]`);
      if (link) {
        link.href = logoPng as unknown as string;
      } else {
        const el = document.createElement('link');
        el.rel = rel as any;
        el.href = logoPng as unknown as string;
        document.head.appendChild(el);
      }
    };
    setIcon('icon');
    setIcon('apple-touch-icon');
  }, []);

  // If user is not authenticated, show login page
  if (!user) {
    return <LoginPage />;
  }

  // If user is authenticated, show main app
  return (
    <RouterWrapper>
      <div style={{ display: 'flex', background: '#1A2030', color: '#fff', minHeight: '100vh' }}>
        <Sidebar onNavChange={setPageTitle} />
        <main style={{ marginLeft: 60, flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100vh' }}>
          <Header pageTitle={pageTitle} />
          <div style={{ flexGrow: 1, overflowY: 'auto', paddingTop: '45px' }}>
            <div style={{ padding: '8px' }}>
              {/* Keep all pages mounted; toggle visibility by current route */}
              <PageMount path="/">
                <HomePage />
              </PageMount>
              <PageMount path="/charts">
                <DrillingChartsPage />
              </PageMount>
              <PageMount path="/card">
                <CardDetailPage />
              </PageMount>
              <StreamsMount />
              {/* Recording player page under Streams section */}
              <PageMount path="/streams/recording" matchPrefix={true}>
                <RecordingPlayerPage />
              </PageMount>
              <PageMount path="/violations">
                <ViolationsPage />
              </PageMount>
              <PageMount path="/analytics">
                <DrillingAnalyticsPage />
              </PageMount>
              <PageMount path="/well">
                <WellPage />
              </PageMount>
              {/* Statistics list page should not show on nested routes */}
              <PageMount path="/statistics" matchPrefix={false}>
                <StatisticsPage />
              </PageMount>
              <PageMount path="/statistics/add">
                <AddReportPage />
              </PageMount>
              <PageMount path="/settings">
                <SettingsPage />
              </PageMount>
              <Routes>
                {/* Routes remain for URL changes; elements are dummies since pages are mounted above */}
                <Route path="/" element={<div />} />
                <Route path="/charts" element={<div />} />
                <Route path="/card/:id" element={<div />} />
                {/* Route exists but content hidden due to always-mounted StreamsPage */}
                <Route path="/streams" element={<div /> } />
                <Route path="/streams/recording/:recordingId" element={<div />} />
                <Route path="/violations" element={<div />} />
                <Route path="/analytics" element={<div />} />
                <Route path="/well" element={<div />} />
                <Route path="/statistics" element={<div />} />
                <Route path="/statistics/add" element={<div />} />
                <Route path="/settings" element={<div />} />
                <Route path="*" element={<div />} />
              </Routes>
            </div>
          </div>
        </main>
      </div>
    </RouterWrapper>
  );
};

const App: FC = () => {
  return (
    <AuthProvider>
      <StreamsProvider>
        <FiltersProvider>
          <AppContent />
        </FiltersProvider>
      </StreamsProvider>
    </AuthProvider>
  );
}

export default App;
